/**
 * useZoneNavigation Hook
 * Manages zone-based navigation (Sidebar, Main/Concierge, TV)
 * Tab key cycles through zones
 */

import { useState, useCallback, useEffect } from 'react';
import type { NavigationZone } from '@/types';

const ZONE_ORDER: NavigationZone[] = ['sidebar', 'main', 'tv'];

interface UseZoneNavigationReturn {
  activeZone: NavigationZone;
  previousZone: NavigationZone;
  setActiveZone: (zone: NavigationZone) => void;
  cycleZone: () => void;
  isZoneActive: (zone: NavigationZone) => boolean;
}

export function useZoneNavigation(
  initialZone: NavigationZone = 'main'
): UseZoneNavigationReturn {
  const [activeZone, setActiveZoneState] = useState<NavigationZone>(initialZone);
  const [previousZone, setPreviousZone] = useState<NavigationZone>(initialZone);

  const setActiveZone = useCallback((zone: NavigationZone) => {
    setActiveZoneState((current) => {
      setPreviousZone(current);
      return zone;
    });
  }, []);

  const cycleZone = useCallback(() => {
    setActiveZoneState((current) => {
      const currentIndex = ZONE_ORDER.indexOf(current);
      const nextIndex = (currentIndex + 1) % ZONE_ORDER.length;
      const nextZone = ZONE_ORDER[nextIndex];
      if (nextZone) {
        setPreviousZone(current);
        return nextZone;
      }
      return current;
    });
  }, []);

  const isZoneActive = useCallback(
    (zone: NavigationZone) => activeZone === zone,
    [activeZone]
  );

  // Handle Tab key to cycle zones - runs first via capture phase
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        e.stopImmediatePropagation(); // Prevent all other handlers
        cycleZone();
      }
    };

    // Use capture phase to ensure Tab works even when views are open
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [cycleZone]);

  return {
    activeZone,
    previousZone,
    setActiveZone,
    cycleZone,
    isZoneActive,
  };
}
